import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/base/Button';
import PhoneInputField from '../../utils/phoneInputField';
import { Alert, Toast } from '../../components/base/Alert';
import { postResource } from '../../services/api';
import logo from '../../assets/images/logo.jpg';

export function Login() {
  const [formData, setFormData] = useState({ telephone: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string; isOpen: boolean }>({
    type: 'info',
    message: '',
    isOpen: false
  });

  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string }>({
    type: 'info',
    title: '',
    message: ''
  });

  const navigate = useNavigate();


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (value: string) => setFormData(prev => ({ ...prev, telephone: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const data = new FormData();
      data.append('telephone', formData.telephone);
      data.append('password', formData.password);

      const response = await postResource('connexion', data);

      if (response.status === 200) {
        localStorage.setItem('access_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        setToast({ type: 'success', message: 'Connexion réussie !', isOpen: true });
        if(response.data.user.role === 'admin'){
            navigate('/admin');
        }else if(response.data.user.role === 'coordinateur regional'){
            navigate('/regional');
        }else if(response.data.user.role === 'coordinateur prefectoral'){
            navigate('/prefectoral');
        }else if(response.data.user.role === 'coordinateur sous-prefectoral'){
            navigate('/sous-prefectoral');
        }else if(response.data.user.role === 'relais paysans pour la victoire (VDP)'){
            navigate('/rpv');
        }
      }
    } catch (error: any) {
      console.log(error);
      setErrors({ messages: error.response?.data?.message || 'Erreur inconnue' });
      setToast({ type: 'error', message: 'Une erreur est survenue lors de la connexion.', isOpen: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-2xl shadow-xl" style={{ backgroundImage: `url(${logo})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain' }}>
          <div className="bg-white p-8 opacity-90 rounded-2xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-green-700 mb-2">AUTHENTIFICATION</h1>
              <span className="font-bold">Voix Des Paysans - VDP</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.messages && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {errors.messages}
                </div>
              )}

              <PhoneInputField value={formData.telephone} onChange={handlePhoneChange} is_required={false} />

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400"></i>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-12 py-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-green-700 text-sm"
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer">
                    <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-gray-600`}></i>
                  </button>
                </div>
              </div>

              <Button type="submit" size="md" className="w-full bg-green-700 hover:bg-green-600 text-white" disabled={isLoading}>
                {isLoading ? <div className="flex items-center justify-center"><i className="ri-loader-4-line animate-spin mr-2"></i>Connexion en cours...</div> : 'Se connecter'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <Toast type={toast.type} message={toast.message} isOpen={toast.isOpen} onClose={() => setToast(prev => ({ ...prev, isOpen: false }))} />
      <Alert type={alert.type} title={alert.title} message={alert.message} isOpen={!!alert.title} onClose={() => setAlert({ type: 'info', title: '', message: '' })} />
    </div>
  );
}

export default Login;
